import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import {
  getProjectRACI,
  createRaci,
  updateRaci,
  deleteRaci,
  getPlantRaci,
  getRaciById,
  getVersions as getVersionsApi,
  addVersion as addVersionApi,
} from "./api";
import {
  getTimesheets,
  createTimesheet,
  updateTimesheet,
  deleteTimesheet,
} from "./timesheetApi";
import { getComments, createComment } from "./commentsApi";
import { secondAxios } from "../../../../../../api/axios";
import { useParams } from "react-router-dom";
import {
  getPlantDocumentList,
  getProjectDocumentList,
} from "./Dashboard/docListAPI";
import { set } from "react-hook-form";
import { useAuthContext } from "../../../../../../context/authContext";
import { useMsal } from "@azure/msal-react";

// Create a context
const DCSContext = createContext();

// Custom hook to use the DCSContext
export const useDCS = () => useContext(DCSContext);

// DCSContext Provider component
export const DCSProvider = ({ children, type, plant }) => {
  const { id, plantId, projectId } = useParams();
  const activeProjectId = projectId || id;
  //console.log("is", id);

  // const { accounts } = useMsal();
  // const username = accounts[0].username;
  // console.log("ttttttttt", username);

  const { user } = useAuthContext();

  // States to handle data
  const [racis, setRacis] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  // const [sharepointDocuments, setSharepointDocuments] = useState([]);
  // const [urnLoaded, setUrnLoaded] = useState(false);
  const [documentList, setDocList] = useState([]);
  const [currentDocsReviewer, setCurrentDocsReviewer] = useState("");
  const [plantProjects, setPlantProjects] = useState([]);
  const [selectedDocuments, setSelectedDocuments] = useState([]);

  // Fetch data on component mount
  const fetchDocumentList = useCallback(
    async (docType) => {
      if (plant && plantId) {
        const list = await getPlantDocumentList(docType, plantId);
        console.log("plantssssss", list);
        setDocList(list);
        return;
      }
      if (activeProjectId) {
        const list = await getProjectDocumentList(docType, activeProjectId);
        setDocList(list);
        return;
      }
      setDocList([]);
    },
    [plant, plantId, activeProjectId]
  );

  const getDocList = useCallback(async () => {
    await fetchDocumentList(type || "all");
  }, [fetchDocumentList, type]);

  const getAllList = useCallback(async () => {
    await fetchDocumentList("all");
  }, [fetchDocumentList]);

  const refreshRaci = useCallback(async () => {
    let racisList = [];

    if (activeProjectId) {
      const racisFromApi = await getProjectRACI(activeProjectId, type);
      console.log("raciiisss", racisFromApi);
      racisList = racisFromApi;
    }
    if (plant && plantId) {
      const plantData = await getPlantRaci(plantId);
      console.log("plant racis from api", plantData);
      let allRacis = plantData?.racis || [];
      // Filter by type if type is provided
      if (type && type !== "all") {
        allRacis = allRacis.filter((raci) => raci.type === type);
      }
      racisList = allRacis;
      setPlantProjects(plantData?.projects || []);
    } else if (!plant) {
      setPlantProjects([]);
    }

    // Enrich RACIs with full data including responsible field
    if (racisList.length > 0) {
      const enrichedRacis = await Promise.all(
        racisList.map(async (raci) => {
          if (raci.id) {
            try {
              const fullRaciData = await getRaciById(raci.id);
              // Merge the full data with existing raci data, preserving any additional fields
              return { ...raci, ...fullRaciData };
            } catch (error) {
              console.error(
                `Error fetching full RACI data for ${raci.id}:`,
                error
              );
              return raci; // Return original if fetch fails
            }
          }
          return raci;
        })
      );
      setRacis(enrichedRacis);
    } else {
      setRacis([]);
    }
  }, [activeProjectId, type, plant, plantId]);

  useEffect(() => {
    refreshRaci();
    const getAllUsers = async () => {
      const users = (await secondAxios.get("/user")).data;
      setAllUsers(users);
    };

    getAllUsers();
    getDocList();
  }, [refreshRaci, getDocList]);

  const addRaci = async (newRaci) => {
    const targetProjectId = newRaci?.projectId || activeProjectId;
    if (!targetProjectId) {
      console.warn("No project id available for creating RACI");
      return;
    }
    await createRaci(targetProjectId, newRaci);
    refreshRaci();
  };

  const updateRaciData = async (raciId, updatedRaci) => {
    await updateRaci(raciId, updatedRaci);
    refreshRaci();
  };

  const deleteRaciData = async (raciId) => {
    // Optimistically remove from state immediately
    setRacis((prev) => (prev || []).filter((raci) => raci.id !== raciId));
    
    try {
      // Make API call in the background
      await deleteRaci(raciId);
    } catch (error) {
      console.error("Error deleting RACI:", error);
      // If API call fails, refresh to restore the item
      // This ensures the UI stays in sync with the server
      refreshRaci();
      throw error; // Re-throw so the caller can handle it
    }
  };

  // Optimistically update users for A/C/I in frontend state
  const updateRaciUsers = (raciId, type, users) => {
    if (!raciId || !type) return;

    const fieldMap = {
      acc: "accountable",
      cons: "consulted",
      inf: "informed",
    };

    const field = fieldMap[type];
    if (!field) return;

    setRacis((prev) =>
      (prev || []).map((raci) =>
        raci.id === raciId
          ? {
              ...raci,
              [field]: users,
            }
          : raci
      )
    );
  };

  const getVersions = async (raciId) => {
    return await getVersionsApi(raciId);
  };

  const addVersion = async (raciId, versionData) => {
    await addVersionApi(raciId, versionData);
    refreshRaci();
  };

  const getTimesheetsData = (id) => getTimesheets(id);
  const createTimesheetData = (id, data) => {
    data.user = user.name;
    createTimesheet(id, data);
  };

  const updateTimesheetData = (id, data) => updateTimesheet(id, data);
  const deleteTimesheetData = (id) => deleteTimesheet(id);

  const getCommentsData = (id) => getComments(id);
  const createCommentData = (data) => createComment(data);

  // Provide all states and functions through the context
  return (
    <DCSContext.Provider
      value={{
        racis,
        allUsers,
        documentList,
        addRaci,
        updateRaciData,
        deleteRaciData,
        getVersions,
        addVersion,
        getTimesheetsData,
        createTimesheetData,
        updateTimesheetData,
        deleteTimesheetData,
        getCommentsData,
        createCommentData,
        type,
        id,
        getDocList,
        getAllList,

        currentDocsReviewer,
        setCurrentDocsReviewer,
        plantProjects,
        plant,
        plantId,
        activeProjectId,
        selectedDocuments,
        setSelectedDocuments,
        updateRaciUsers,
      }}
    >
      {children}
    </DCSContext.Provider>
  );
};
